/**
 * In-memory cache of signed image-proxy URLs (authenticated app).
 * Renewed proactively so images do not disappear without user action.
 */

import axios from 'axios';
import { extractS3KeyFromUrl } from './s3ObjectKey';

/** Refresh signatures this long before they expire (30 min). */
const RENEW_BUFFER_MS = 30 * 60 * 1000;

/** Background renewal interval (2 h). */
const RENEW_INTERVAL_MS = 2 * 60 * 60 * 1000;

const cache = new Map();
const listeners = new Set();
let renewTimer = null;
let domListenersInstalled = false;

function extractKeyFromProxyUrl(url) {
  if (!url || typeof url !== 'string' || !url.includes('/image-proxy?')) {
    return null;
  }
  try {
    const query = url.includes('?') ? url.split('?').pop() : '';
    return new URLSearchParams(query).get('key');
  } catch {
    return null;
  }
}

function refreshDomProxyImages() {
  if (typeof document === 'undefined') {
    return;
  }
  const images = document.querySelectorAll('img[src*="/image-proxy?"]');
  images.forEach((img) => {
    const key = extractKeyFromProxyUrl(img.src);
    if (!key) {
      return;
    }
    const fresh = getCachedSignedProxyUrl(key);
    if (fresh && fresh !== img.src) {
      img.src = fresh;
    }
  });
}

function parseExpMsFromSignedUrl(url) {
  if (!url || typeof url !== 'string' || !url.includes('/image-proxy?')) {
    return null;
  }
  try {
    const query = url.includes('?') ? url.split('?').pop() : '';
    const params = new URLSearchParams(query);
    const exp = Number(params.get('exp'));
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function notifyListeners() {
  refreshDomProxyImages();
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

function isFresh(entry) {
  return entry && entry.expMs > Date.now() + RENEW_BUFFER_MS;
}

export function getCachedSignedProxyUrl(key) {
  if (!key) return null;
  if (typeof key === 'string' && key.includes('/image-proxy?')) {
    return key;
  }
  const entry = cache.get(key);
  return isFresh(entry) ? entry.url : null;
}

export function subscribeSignedProxyCache(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const MEDIA_KEY_FIELDS = /^key(_|$)/;
const DISPLAY_URL_FIELDS = /^display_url/;

function collectS3KeysFromValue(value, keysSet) {
  if (value == null) return;
  if (Array.isArray(value)) {
    value.forEach((item) => collectS3KeysFromValue(item, keysSet));
    return;
  }
  if (typeof value !== 'object') return;

  for (const [field, nested] of Object.entries(value)) {
    if (typeof nested === 'string') {
      if (MEDIA_KEY_FIELDS.test(field) && nested.length > 0 && !nested.includes('/image-proxy?')) {
        keysSet.add(nested);
      }
      if (DISPLAY_URL_FIELDS.test(field) && nested.includes('amazonaws.com')) {
        const extracted = extractS3KeyFromUrl(nested);
        if (extracted) {
          keysSet.add(extracted);
        }
      }
    } else {
      collectS3KeysFromValue(nested, keysSet);
    }
  }
}

export function collectS3KeysFromPayload(payload) {
  const keys = new Set();
  collectS3KeysFromValue(payload, keys);
  return [...keys];
}

export async function ensureSignedProxyUrls(keys, { silent = false } = {}) {
  const unique = [...new Set((keys || []).filter(Boolean))];
  const needsSign = unique.filter((key) => {
    if (typeof key === 'string' && key.includes('/image-proxy?')) {
      return false;
    }
    return !isFresh(cache.get(key));
  });

  if (!needsSign.length) {
    return;
  }

  try {
    const { data } = await axios.post('/image-proxy/sign', { keys: needsSign });
    const urls = data?.urls || {};
    for (const [key, url] of Object.entries(urls)) {
      cache.set(key, {
        url,
        expMs: parseExpMsFromSignedUrl(url) || Date.now() + 4 * 60 * 60 * 1000,
      });
    }
    notifyListeners();
  } catch (error) {
    if (!silent) {
      console.warn('Signed image-proxy URL batch failed', error);
    }
  }
}

export async function warmSignedProxyCacheFromPayload(payload) {
  const keys = collectS3KeysFromPayload(payload);
  if (keys.length) {
    await ensureSignedProxyUrls(keys, { silent: true });
  }
}

async function renewExpiringSignatures() {
  const keysToRenew = [];
  for (const [key, entry] of cache.entries()) {
    if (!isFresh(entry)) {
      keysToRenew.push(key);
    }
  }
  if (keysToRenew.length) {
    await ensureSignedProxyUrls(keysToRenew, { silent: true });
  }
}

export function startSignedProxyCacheRenewal() {
  if (renewTimer) return;
  renewTimer = setInterval(() => {
    renewExpiringSignatures();
  }, RENEW_INTERVAL_MS);

  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      renewExpiringSignatures();
    }
  };
  document.addEventListener('visibilitychange', onVisible);

  if (!domListenersInstalled && typeof document !== 'undefined') {
    domListenersInstalled = true;
    document.addEventListener(
      'error',
      (event) => {
        const el = event.target;
        const tag = el?.tagName;
        if ((tag === 'IMG' || tag === 'VIDEO' || tag === 'SOURCE') && el.src?.includes('/image-proxy?')) {
          handleMediaElementError(event, {
            resolveKey: (media) => extractKeyFromProxyUrl(media.src),
          });
        }
      },
      true,
    );
  }
}

/** One-shot retry when an <img> fails (e.g. signature just expired). */
export async function refreshSignedUrlForKey(key) {
  if (!key || key.includes('/image-proxy?')) {
    return key;
  }
  cache.delete(key);
  await ensureSignedProxyUrls([key], { silent: true });
  return getCachedSignedProxyUrl(key);
}

export function handleMediaElementError(event, { resolveKey } = {}) {
  // Delegated document listeners have currentTarget === document (no dataset).
  const el = event?.target || event?.currentTarget;
  if (!el?.dataset || el.dataset.proxyRetried === '1') {
    return;
  }
  const key = typeof resolveKey === 'function' ? resolveKey(el) : el.dataset.s3Key;
  if (!key) {
    return;
  }
  el.dataset.proxyRetried = '1';
  refreshSignedUrlForKey(key).then((url) => {
    if (url) {
      el.src = url;
    }
  });
}
